import React, { useState } from 'react';
import { apiClient } from '../../../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
  BG_CARD,
  FONT_HEADING,
} from '../designTokens';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunEntry {
  id: string;
  guestId: string;
  tableId: string;
  seatId: string;
  seatNumber: number;
  tableNumber: number;
  zoneName: string;
  ruleThatPlaced: string;
  groupLabel: string | null;
  status: string;
  guest: { id: string; fullName: string; ticketNumber: string | null };
  table: { id: string; tableNumber: number; tableName: string | null };
}

interface AssignmentRun {
  id: string;
  status: string;
  totalProcessed: number;
  totalAssigned: number;
  totalUnplaced: number;
  warnings: string[];
  entries: RunEntry[];
  createdBy?: { id: string; fullName: string };
}

interface ReviewIssue {
  severity: string;
  type: string;
  description: string;
  recommendation: string;
}

interface BatchResultsViewProps {
  run: AssignmentRun;
  onApproved: () => void;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {},
  summaryBar: {
    display: 'flex',
    gap: '1.25rem',
    marginBottom: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: '1.1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
  },
  summaryLabel: {
    fontSize: '0.68rem',
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
  },
  tableGroup: {
    marginBottom: '0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.55rem 0.85rem',
    backgroundColor: BG_SURFACE,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  tableTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  countBadge: {
    fontSize: '0.68rem',
    fontWeight: 600,
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '0.15rem 0.4rem',
    borderRadius: '3px',
  },
  guestRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.45rem 0.85rem',
    fontSize: '0.8rem',
    borderBottom: `1px solid #f5f5f5`,
    gap: '0.75rem',
  },
  guestName: {
    flex: 1,
    fontWeight: 500,
    color: TEXT_DARK,
  },
  seatNum: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    width: '60px',
  },
  ruleTag: {
    fontSize: '0.68rem',
    color: '#6a1b9a',
    backgroundColor: '#f3e5f5',
    padding: '0.1rem 0.3rem',
    borderRadius: '3px',
  },
  groupTag: {
    fontSize: '0.68rem',
    color: '#e65100',
    backgroundColor: '#fff3e0',
    padding: '0.1rem 0.3rem',
    borderRadius: '3px',
  },
  releaseBtn: {
    padding: '0.2rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '3px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    color: TEXT_MUTED,
  },
  releasedBtn: {
    padding: '0.2rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    border: '1px solid #ef9a9a',
    borderRadius: '3px',
    backgroundColor: '#fdecea',
    cursor: 'pointer',
    color: '#d32f2f',
  },
  warningsSection: {
    marginTop: '1rem',
    border: `1px solid #ffcc80`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  warningsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fff8e1',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#e65100',
  },
  warningItem: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.78rem',
    color: '#bf360c',
    borderBottom: '1px solid #ffe0b2',
  },
  unplacedSection: {
    marginTop: '1rem',
  },
  unplacedTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#d32f2f',
    marginBottom: '0.5rem',
  },
  unplacedItem: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    color: '#555',
    backgroundColor: '#fdecea',
    borderBottom: '1px solid #fce4ec',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1.25rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  approveBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  approveSelectedBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  aiReviewBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  success: {
    padding: '0.5rem 0.75rem',
    color: '#2e7d32',
    fontSize: '0.8rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  // AI Review styles
  reviewSection: {
    marginTop: '1rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  reviewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.55rem 0.85rem',
    backgroundColor: '#e3f2fd',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#1565c0',
  },
  reviewBody: {
    padding: '0.5rem 0.85rem',
  },
  reviewIssueCard: {
    padding: '0.6rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    marginBottom: '0.5rem',
    backgroundColor: BG_CARD,
  },
  reviewIssueHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.3rem',
  },
  severityBadge: {
    fontSize: '0.68rem',
    padding: '0.1rem 0.35rem',
    borderRadius: '3px',
    fontWeight: 600,
  },
  issueDescription: {
    fontSize: '0.8rem',
    color: TEXT_DARK,
    marginBottom: '0.25rem',
  },
  issueRecommendation: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  dismissIssueBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 6px',
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    borderRadius: '3px',
  },
  noIssues: {
    padding: '0.75rem',
    textAlign: 'center' as const,
    color: '#2e7d32',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
};

const severityStyles: Record<string, { bg: string; color: string }> = {
  Warning: { bg: '#fff3e0', color: '#e65100' },
  Suggestion: { bg: '#e3f2fd', color: '#1565c0' },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function BatchResultsView({ run, onApproved }: BatchResultsViewProps) {
  const [releasedEntryIds, setReleasedEntryIds] = useState<Set<string>>(new Set());
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // AI Review state
  const [reviewIssues, setReviewIssues] = useState<ReviewIssue[] | null>(null);
  const [reviewExpanded, setReviewExpanded] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [dismissedIssues, setDismissedIssues] = useState<Set<number>>(new Set());

  // Group entries by table
  const entriesByTable = run.entries.reduce<Record<number, RunEntry[]>>((acc, entry) => {
    const tNum = entry.tableNumber;
    if (!acc[tNum]) acc[tNum] = [];
    acc[tNum].push(entry);
    return acc;
  }, {});

  const sortedTableNumbers = Object.keys(entriesByTable)
    .map(Number)
    .sort((a, b) => a - b);

  const toggleRelease = (entryId: string) => {
    setReleasedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const handleApproveAll = async () => {
    setError('');
    setLoading(true);
    try {
      await apiClient(`/batch-assignment/runs/${run.id}/approve`, {
        method: 'POST',
        body: { releasedEntryIds: [] },
      });
      setSuccess('All assignments approved and applied successfully.');
      onApproved();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to approve run.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSelected = async () => {
    setError('');
    setLoading(true);
    try {
      await apiClient(`/batch-assignment/runs/${run.id}/approve`, {
        method: 'POST',
        body: { releasedEntryIds: Array.from(releasedEntryIds) },
      });
      setSuccess('Approved with selected releases applied.');
      onApproved();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to approve run.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Reject all assignments? This will discard the entire batch run.')) return;
    setError('');
    setLoading(true);
    try {
      await apiClient(`/batch-assignment/runs/${run.id}/reject`, {
        method: 'POST',
      });
      setSuccess('Run rejected. No seat assignments were applied.');
      onApproved();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to reject run.');
    } finally {
      setLoading(false);
    }
  };

  const handleAiReview = async () => {
    setReviewLoading(true);
    setError('');
    try {
      const data = await apiClient<{ runId: string; issueCount: number; issues: ReviewIssue[] }>(
        `/batch-assignment/runs/${run.id}/ai-review`,
        { method: 'POST' },
      );
      setReviewIssues(data.issues);
      setDismissedIssues(new Set());
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to run AI review.');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleDismissIssue = (index: number) => {
    setDismissedIssues((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const isActionable = run.status === 'Completed';
  const canReview = run.status === 'Completed' || run.status === 'Approved' || run.status === 'PartiallyApproved';
  const hasReleased = releasedEntryIds.size > 0;
  const visibleIssues = reviewIssues
    ? reviewIssues.filter((_, idx) => !dismissedIssues.has(idx))
    : null;

  return (
    <div style={styles.container}>
      {/* Summary header */}
      <div style={styles.summaryBar} role="region" aria-label="Run results summary">
        <div style={styles.summaryItem}>
          <span style={styles.summaryValue}>{run.totalProcessed}</span>
          <span style={styles.summaryLabel}>Processed</span>
        </div>
        <div style={styles.summaryItem}>
          <span style={{ ...styles.summaryValue, color: '#2e7d32' }}>{run.totalAssigned}</span>
          <span style={styles.summaryLabel}>Assigned</span>
        </div>
        <div style={styles.summaryItem}>
          <span style={{ ...styles.summaryValue, color: '#d32f2f' }}>{run.totalUnplaced}</span>
          <span style={styles.summaryLabel}>Unplaced</span>
        </div>
        <div style={styles.summaryItem}>
          <span style={{ ...styles.summaryValue, color: '#e65100' }}>{run.warnings.length}</span>
          <span style={styles.summaryLabel}>Warnings</span>
        </div>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {success && <div style={styles.success} role="status">{success}</div>}

      {/* Entries grouped by table */}
      {sortedTableNumbers.map((tableNum) => {
        const entries = entriesByTable[tableNum];
        const zoneName = entries[0]?.zoneName || '';
        return (
          <div key={tableNum} style={styles.tableGroup}>
            <div style={styles.tableHeader}>
              <span style={styles.tableTitle}>
                Table {tableNum}{zoneName ? ` — ${zoneName}` : ''}
              </span>
              <span style={styles.countBadge}>{entries.length} guests</span>
            </div>
            {entries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  ...styles.guestRow,
                  backgroundColor: releasedEntryIds.has(entry.id) ? '#fdecea' : '#fff',
                }}
              >
                <span style={styles.guestName}>{entry.guest.fullName}</span>
                <span style={styles.seatNum}>Seat {entry.seatNumber}</span>
                {entry.ruleThatPlaced && (
                  <span style={styles.ruleTag}>{entry.ruleThatPlaced}</span>
                )}
                {entry.groupLabel && (
                  <span style={styles.groupTag}>{entry.groupLabel}</span>
                )}
                {isActionable && (
                  <button
                    style={releasedEntryIds.has(entry.id) ? styles.releasedBtn : styles.releaseBtn}
                    onClick={() => toggleRelease(entry.id)}
                    aria-label={`${releasedEntryIds.has(entry.id) ? 'Restore' : 'Release'} ${entry.guest.fullName}`}
                  >
                    {releasedEntryIds.has(entry.id) ? 'Released' : 'Release'}
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {/* Warnings */}
      {run.warnings.length > 0 && (
        <div style={styles.warningsSection}>
          <div
            style={styles.warningsHeader}
            onClick={() => setWarningsExpanded(!warningsExpanded)}
            role="button"
            aria-expanded={warningsExpanded}
          >
            <span>⚠ Warnings ({run.warnings.length})</span>
            <span>{warningsExpanded ? '▾' : '▸'}</span>
          </div>
          {warningsExpanded && (
            <div>
              {run.warnings.map((warning, idx) => (
                <div key={idx} style={styles.warningItem}>{warning}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unplaced guests */}
      {run.totalUnplaced > 0 && (
        <div style={styles.unplacedSection}>
          <div style={styles.unplacedTitle}>
            Unplaced Guests ({run.totalUnplaced})
          </div>
          <div style={{ border: '1px solid #ef9a9a', borderRadius: '6px', overflow: 'hidden' }}>
            {run.entries
              .filter((e) => e.status === 'Released')
              .map((entry) => (
                <div key={entry.id} style={styles.unplacedItem}>
                  {entry.guest.fullName}
                </div>
              ))}
            {run.entries.filter((e) => e.status === 'Released').length === 0 && (
              <div style={styles.unplacedItem}>
                {run.totalUnplaced} guest(s) could not be placed by the engine.
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Review Section */}
      {reviewIssues !== null && (
        <div style={styles.reviewSection}>
          <div
            style={styles.reviewHeader}
            onClick={() => setReviewExpanded(!reviewExpanded)}
            role="button"
            aria-expanded={reviewExpanded}
          >
            <span>🤖 AI Review ({visibleIssues?.length ?? 0} issues)</span>
            <span>{reviewExpanded ? '▾' : '▸'}</span>
          </div>
          {reviewExpanded && (
            <div style={styles.reviewBody}>
              {visibleIssues && visibleIssues.length === 0 && (
                <div style={styles.noIssues}>✓ No issues found</div>
              )}
              {visibleIssues &&
                visibleIssues.map((issue) => {
                  const actualIdx = reviewIssues!.indexOf(issue);
                  const severity = severityStyles[issue.severity] ?? severityStyles.Suggestion;
                  return (
                    <div key={actualIdx} style={styles.reviewIssueCard}>
                      <div style={styles.reviewIssueHeader}>
                        <span
                          style={{
                            ...styles.severityBadge,
                            backgroundColor: severity.bg,
                            color: severity.color,
                          }}
                        >
                          {issue.severity}
                        </span>
                        <button
                          style={styles.dismissIssueBtn}
                          onClick={() => handleDismissIssue(actualIdx)}
                          aria-label={`Dismiss issue: ${issue.description}`}
                          title="Dismiss"
                        >
                          ✕
                        </button>
                      </div>
                      <div style={styles.issueDescription}>{issue.description}</div>
                      <div style={styles.issueRecommendation}>{issue.recommendation}</div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {isActionable && !success && (
        <div style={styles.actions}>
          <button
            style={{ ...styles.approveBtn, opacity: loading ? 0.7 : 1 }}
            onClick={handleApproveAll}
            disabled={loading}
            aria-label="Approve all assignments"
          >
            Approve All
          </button>
          {hasReleased && (
            <button
              style={{ ...styles.approveSelectedBtn, opacity: loading ? 0.7 : 1 }}
              onClick={handleApproveSelected}
              disabled={loading}
              aria-label="Approve selected assignments"
            >
              Approve Selected ({run.entries.length - releasedEntryIds.size} of {run.entries.length})
            </button>
          )}
          <button
            style={{ ...styles.rejectBtn, opacity: loading ? 0.7 : 1 }}
            onClick={handleReject}
            disabled={loading}
            aria-label="Reject all assignments"
          >
            Reject All
          </button>
          {canReview && reviewIssues === null && (
            <button
              style={{ ...styles.aiReviewBtn, opacity: reviewLoading ? 0.7 : 1 }}
              onClick={handleAiReview}
              disabled={reviewLoading}
              aria-label="Run AI review on this assignment"
            >
              {reviewLoading ? 'Reviewing...' : '🤖 AI Review'}
            </button>
          )}
        </div>
      )}

      {/* AI Review button when run is already approved/completed but we haven't reviewed yet */}
      {!isActionable && canReview && !success && reviewIssues === null && (
        <div style={{ ...styles.actions, borderTop: 'none', paddingTop: '0.5rem' }}>
          <button
            style={{ ...styles.aiReviewBtn, opacity: reviewLoading ? 0.7 : 1 }}
            onClick={handleAiReview}
            disabled={reviewLoading}
            aria-label="Run AI review on this assignment"
          >
            {reviewLoading ? 'Reviewing...' : '🤖 AI Review'}
          </button>
        </div>
      )}
    </div>
  );
}
